import React, { useState, useEffect } from 'react';
import { HtmlRenderer } from './HtmlRenderer';

import { useTranslation } from 'react-i18next';

interface HtmlFromUrlRendererProps {
  url: string;
  title?: string;
}

/**
 * Fetches an HTML document from a URL (e.g. a short artifact URL) and renders
 * it using the same sandboxed iframe infrastructure as HtmlRenderer.
 *
 * Used for workspaceGetDownloadLink results where the file is a *.pleri.html artifact.
 */
export const HtmlFromUrlRenderer: React.FC<HtmlFromUrlRendererProps> = ({
  url,
  title = 'HTML document',
}) => {
  const { t } = useTranslation('pleri/chat');
  const [html, setHtml] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then((r) => {
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }
        return r.text();
      })
      .then((text) => {
        if (!cancelled) {
          setHtml(text);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400">
        <svg
          className="h-4 w-4 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.75"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
          />
        </svg>
        {t('failedToLoadArtifact', { error })}
      </div>
    );
  }

  // While fetching, pass undefined html — HtmlRenderer shows its skeleton
  return <HtmlRenderer html={html} title={title} isStreaming={false} />;
};
